import React from 'react';
import { Tag, Typography } from 'antd';
import { DVIInspectionItem } from '../../types/dvi';
import { formatMeasurement } from './notePresets';

const { Text } = Typography;

export interface InspectionNotesSummaryProps {
  items: DVIInspectionItem[];
  /** Heading shown above the list; omit to render the rows bare. */
  title?: string;
}

/**
 * Read-only rendering of inspection NOTES and CONDITION values (WP-07).
 *
 * Deliberately presentational and dependency-free so the same markup can back
 * the invoice, the customer approval view, and whatever WP-10 builds — the
 * WP-07 brief asks for these to read consistently in all three places, and
 * three separate hand-rolled renderings is how they drift apart.
 *
 * WHAT IT SHOWS. For each item with something to say: the point name, its
 * condition tag, the measurement prefixed by the SHOP'S OWN LABEL ("Wear: 2mm",
 * "Damage Location: front bumper"), and the note. The label comes from
 * `measurement_label` on the item — a snapshot taken when the value was
 * entered — so an invoice reprinted next year still says what it said the day
 * it was written, even if the preset has since been renamed or retired.
 */
const statusTag = (status: string) => {
  if (status === 'pass') return <Tag color="success">Pass</Tag>;
  if (status === 'attention') return <Tag color="warning">Attention</Tag>;
  if (status === 'fail') return <Tag color="error">Fail</Tag>;
  return null; // 'pending' — nothing decided, nothing to assert
};

/** An item earns a row only if it carries a judgement, a note, or a value. */
export function hasReportableContent(it: DVIInspectionItem): boolean {
  return (
    it.status !== 'pending' ||
    !!(it.notes && it.notes.trim() !== '') ||
    !!(it.reading_value && it.reading_value.trim() !== '')
  );
}

export const InspectionNotesSummary: React.FC<InspectionNotesSummaryProps> = ({
  items,
  title,
}) => {
  const visible = (items ?? []).filter(hasReportableContent);
  if (visible.length === 0) return null;

  // Group by category, preserving the order the items arrived in.
  const grouped = new Map<string, DVIInspectionItem[]>();
  for (const it of visible) {
    const list = grouped.get(it.category_name) ?? [];
    list.push(it);
    grouped.set(it.category_name, list);
  }

  return (
    <div data-testid="inspection-notes-summary">
      {title && (
        <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 15 }}>
          {title}
        </Text>
      )}
      {Array.from(grouped.entries()).map(([category, catItems]) => (
        <div key={category} style={{ marginBottom: 12 }}>
          <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
            {category}
          </Text>
          {catItems.map((it) => {
            const value = it.reading_value?.trim();
            return (
              <div
                key={it.id}
                style={{
                  padding: '6px 10px',
                  border: '1px solid #e5e7eb',
                  borderRadius: 6,
                  marginBottom: 6,
                }}
              >
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Text style={{ fontWeight: 500 }}>{it.point_name}</Text>
                  {statusTag(it.status)}
                  {value && (
                    <Text strong data-testid={`summary-measurement-${it.id}`}>
                      {formatMeasurement(value, it.unit, it.measurement_label)}
                    </Text>
                  )}
                </div>
                {it.notes && it.notes.trim() !== '' && (
                  <Text
                    type="secondary"
                    data-testid={`summary-note-${it.id}`}
                    style={{ fontSize: 12, display: 'block', marginTop: 2 }}
                  >
                    {it.notes}
                  </Text>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default InspectionNotesSummary;
